import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth'
import {
  api, ApiFailure, ageLabel, genderShort, computeQuantity,
  MEDICINE_TIMING_LABEL, TEST_URGENCY_LABEL, VISIT_TYPE_LABEL,
  type ClinicSettings, type Doctor, type Drug, type HistoryEntry, type Letterhead,
  type MedicineTiming, type Patient, type Prescription, type PrescriptionTemplate,
  type QueueRow, type TestMasterRow, type TestUrgency, type Visit, type Vitals,
} from '../api'
import { CategoryPill } from '../components/PatientCategory'
import Autocomplete from '../components/Autocomplete'
import PrescriptionPrint from '../components/PrescriptionPrint'
import {
  allergyConflicts, describeConflicts, medicineLabel, vitalFlags, vitalClass,
  type AllergyConflict, type VitalFlag,
} from '../clinical'

interface PadDetail {
  visit: Visit
  patient: Patient
  doctor: Doctor
  vitals: Vitals[]
  prescription: Prescription | null
}

interface MedRow {
  key: string
  drugId: string | null
  drugName: string
  /** Kept only so the allergy check can see past a brand name to the molecule. */
  genericName: string
  strength: string
  form: string
  doseMorning: string
  doseAfternoon: string
  doseNight: string
  timing: MedicineTiming | ''
  durationDays: string
  quantity: string
  instructions: string
}

interface TestRow {
  key: string
  testId: string | null
  testName: string
  urgency: TestUrgency
  instructions: string
  preparation: string | null
}

interface PadState {
  chiefComplaints: string
  historyPresentIllness: string
  pastHistory: string
  familyHistory: string
  examinationFindings: string
  provisionalDiagnosis: string
  icd10Code: string
  advice: string
  followUpDays: string
  medicines: MedRow[]
  tests: TestRow[]
}

const newKey = () => Math.random().toString(36).slice(2)

function blankMed(): MedRow {
  return {
    key: newKey(), drugId: null, drugName: '', genericName: '', strength: '', form: '',
    doseMorning: '', doseAfternoon: '', doseNight: '', timing: '',
    durationDays: '', quantity: '', instructions: '',
  }
}

function blankTest(): TestRow {
  return {
    key: newKey(), testId: null, testName: '', urgency: 'routine',
    instructions: '', preparation: null,
  }
}

const BLANK_PAD: PadState = {
  chiefComplaints: '', historyPresentIllness: '', pastHistory: '', familyHistory: '',
  examinationFindings: '', provisionalDiagnosis: '', icd10Code: '', advice: '',
  followUpDays: '', medicines: [blankMed()], tests: [blankTest()],
}

/** Keeps exactly one blank row at the end, so there is never an "add" click. */
function padRows<T extends { key: string }>(rows: T[], isBlank: (r: T) => boolean, make: () => T): T[] {
  const filled = rows.filter((r) => !isBlank(r))
  return [...filled, ...(rows.some(isBlank) ? rows.filter(isBlank).slice(-1) : [make()])]
}

const medBlank = (m: MedRow) => m.drugName.trim() === ''
const testBlank = (t: TestRow) => t.testName.trim() === ''

function fromPrescription(rx: Prescription | null): PadState {
  if (!rx) return { ...BLANK_PAD, medicines: [blankMed()], tests: [blankTest()] }
  return {
    chiefComplaints: rx.chiefComplaints ?? '',
    historyPresentIllness: rx.historyPresentIllness ?? '',
    pastHistory: rx.pastHistory ?? '',
    familyHistory: rx.familyHistory ?? '',
    examinationFindings: rx.examinationFindings ?? '',
    provisionalDiagnosis: rx.provisionalDiagnosis ?? '',
    icd10Code: rx.icd10Code ?? '',
    advice: rx.advice ?? '',
    followUpDays: rx.followUpDays != null ? String(rx.followUpDays) : '',
    medicines: [
      ...rx.medicines.map((m) => ({
        key: newKey(),
        drugId: m.drugId, drugName: m.drugName, genericName: '',
        strength: m.strength ?? '', form: m.form ?? '',
        doseMorning: m.doseMorning ?? '', doseAfternoon: m.doseAfternoon ?? '',
        doseNight: m.doseNight ?? '',
        timing: (m.timing ?? '') as MedicineTiming | '',
        durationDays: m.durationDays != null ? String(m.durationDays) : '',
        quantity: m.quantity ?? '', instructions: m.instructions ?? '',
      })),
      blankMed(),
    ],
    tests: [
      ...rx.tests.map((t) => ({
        key: newKey(), testId: t.testId, testName: t.testName,
        urgency: t.urgency, instructions: t.instructions ?? '', preparation: null,
      })),
      blankTest(),
    ],
  }
}

export default function Console() {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()

  const [doctorList, setDoctorList] = useState<Doctor[]>([])
  const [doctorId, setDoctorId] = useState<string>('')
  const [queue, setQueue] = useState<QueueRow[]>([])
  const [detail, setDetail] = useState<PadDetail | null>(null)
  const [pad, setPad] = useState<PadState>(BLANK_PAD)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [openEntry, setOpenEntry] = useState<string | null>(null)
  const [templates, setTemplates] = useState<PrescriptionTemplate[]>([])
  const [templateName, setTemplateName] = useState('')
  const [clinic, setClinic] = useState<ClinicSettings | null>(null)
  const [letterhead, setLetterhead] = useState<Letterhead | null>(null)
  const [addendum, setAddendum] = useState('')
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState(false)
  const [printAt, setPrintAt] = useState(0)

  const visitId = params.get('visit')
  const locked = detail?.prescription?.isLocked ?? false

  /* Printing has to wait for the signed prescription to reach the DOM, so it
     runs from an effect rather than straight after the request. */
  useEffect(() => {
    if (printAt) window.print()
  }, [printAt])

  useEffect(() => {
    void api.get<{ doctors: Doctor[] }>('/api/doctors').then((r) => setDoctorList(r.doctors))
    void api.get<{ settings: ClinicSettings | null }>('/api/settings').then((r) => setClinic(r.settings))
  }, [])

  // A doctor only ever sees their own queue; an admin picks whose room they
  // are standing in.
  useEffect(() => {
    if (user?.doctorId) setDoctorId(user.doctorId)
    else if (doctorList.length > 0) setDoctorId((d) => d || doctorList[0]!.id)
  }, [user, doctorList])

  useEffect(() => {
    if (!doctorId) return
    void api.get<{ doctor: Doctor; letterhead: Letterhead | null }>(`/api/doctors/${doctorId}`)
      .then((r) => setLetterhead(r.letterhead))
      .catch(() => setLetterhead(null))
    void api.get<{ templates: PrescriptionTemplate[] }>(`/api/templates?doctorId=${doctorId}`)
      .then((r) => setTemplates(r.templates))
      .catch(() => setTemplates([]))
  }, [doctorId])

  const loadQueue = useCallback(async () => {
    if (!doctorId) return
    const r = await api.get<{ visits: QueueRow[] }>(`/api/visits?doctorId=${doctorId}`)
    setQueue(r.visits)
  }, [doctorId])

  useEffect(() => { void loadQueue() }, [loadQueue])

  const loadPad = useCallback(async (id: string) => {
    const res = await api.get<PadDetail>(`/api/visits/${id}/prescription`)
    setDetail(res)
    setPad(fromPrescription(res.prescription))
    setAddendum('')
    setPreview(false)
    const h = await api.get<{ history: HistoryEntry[] }>(
      `/api/patients/${res.patient.id}/history?excludeVisitId=${id}`,
    )
    setHistory(h.history)
    setOpenEntry(null)
  }, [])

  useEffect(() => {
    if (!visitId) {
      setDetail(null)
      return
    }
    void loadPad(visitId).catch((err) => {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not open that visit.' })
    })
  }, [visitId, loadPad])

  async function openVisit(row: QueueRow) {
    setMsg(null)
    setParams({ visit: row.id }, { replace: true })
    // Taking a patient in marks the visit in consult, which is what stops
    // reception from opening the same visit behind you.
    if (row.status === 'waiting') {
      try {
        await api.patch(`/api/visits/${row.id}`, { status: 'in_consult' })
        await loadQueue()
      } catch { /* the pad still opens; the queue label is cosmetic */ }
    }
  }

  function setMed(key: string, patch: Partial<MedRow>) {
    setPad((p) => ({
      ...p,
      medicines: padRows(
        p.medicines.map((m) => (m.key === key ? { ...m, ...patch } : m)),
        medBlank, blankMed,
      ),
    }))
  }

  function setTest(key: string, patch: Partial<TestRow>) {
    setPad((p) => ({
      ...p,
      tests: padRows(
        p.tests.map((t) => (t.key === key ? { ...t, ...patch } : t)),
        testBlank, blankTest,
      ),
    }))
  }

  function dropMed(key: string) {
    setPad((p) => ({
      ...p,
      medicines: padRows(p.medicines.filter((m) => m.key !== key), medBlank, blankMed),
    }))
  }

  function dropTest(key: string) {
    setPad((p) => ({
      ...p,
      tests: padRows(p.tests.filter((t) => t.key !== key), testBlank, blankTest),
    }))
  }

  const payload = useMemo(() => ({
    chiefComplaints: pad.chiefComplaints || null,
    historyPresentIllness: pad.historyPresentIllness || null,
    pastHistory: pad.pastHistory || null,
    familyHistory: pad.familyHistory || null,
    examinationFindings: pad.examinationFindings || null,
    provisionalDiagnosis: pad.provisionalDiagnosis || null,
    icd10Code: pad.icd10Code || null,
    advice: pad.advice || null,
    followUpDays: pad.followUpDays ? Number(pad.followUpDays) : null,
    medicines: pad.medicines.filter((m) => !medBlank(m)).map((m) => ({
      drugId: m.drugId,
      drugName: m.drugName.trim(),
      strength: m.strength || null,
      form: m.form || null,
      doseMorning: m.doseMorning || null,
      doseAfternoon: m.doseAfternoon || null,
      doseNight: m.doseNight || null,
      timing: m.timing || null,
      durationDays: m.durationDays ? Number(m.durationDays) : null,
      quantity: m.quantity || null,
      instructions: m.instructions || null,
    })),
    tests: pad.tests.filter((t) => !testBlank(t)).map((t) => ({
      testId: t.testId,
      testName: t.testName.trim(),
      urgency: t.urgency,
      instructions: t.instructions || null,
      reviewOn: null,
    })),
  }), [pad])

  async function run(what: () => Promise<void>, ok: string) {
    setBusy(true)
    setMsg(null)
    try {
      await what()
      if (ok) setMsg({ kind: 'ok', text: ok })
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not save.' })
    } finally {
      setBusy(false)
    }
  }

  const saveDraft = () => run(async () => {
    const r = await api.put<{ prescription: Prescription }>(
      `/api/visits/${visitId}/prescription`, payload,
    )
    setDetail((d) => (d ? { ...d, prescription: r.prescription } : d))
  }, 'Draft saved.')

  /**
   * Signing is irreversible and puts paper in the patient's hand, so an
   * allergy collision gets one deliberate confirmation first. The check is a
   * name match, not a clinical ruling, so the doctor always has the last word.
   */
  const signAndPrint = () => run(async () => {
    if (conflictSummary.length > 0) {
      const ok = window.confirm(
        `${detail?.patient.fullName} is recorded as allergic to `
        + `${detail?.patient.allergies}.\n\n`
        + `${conflictSummary.join('\n')}\n\n`
        + 'Sign and print anyway?',
      )
      if (!ok) return
    }
    const r = await api.post<{ prescription: Prescription }>(
      `/api/visits/${visitId}/prescription/sign`, payload,
    )
    const p = await api.post<{ prescription: Prescription }>(
      `/api/visits/${visitId}/prescription/print`,
    )
    setDetail((d) => (d ? { ...d, prescription: p.prescription ?? r.prescription } : d))
    await loadQueue()
    setPrintAt(Date.now())
  }, 'Signed. Sending to the printer.')

  const printAgain = () => run(async () => {
    const r = await api.post<{ prescription: Prescription }>(
      `/api/visits/${visitId}/prescription/print`,
    )
    setDetail((d) => (d ? { ...d, prescription: r.prescription } : d))
    setPrintAt(Date.now())
  }, '')

  const reopenForEdit = () => {
    const reason = window.prompt(
      'Reopen for edit. Why? (this goes on the record)',
      'Correction to prescription',
    )
    if (!reason?.trim()) return
    void run(async () => {
      const r = await api.post<{ prescription: Prescription }>(
        `/api/visits/${visitId}/prescription/reopen`, { reason: reason.trim() },
      )
      setDetail((d) => (d ? { ...d, prescription: r.prescription } : d))
      await loadQueue()
    }, 'Reopened. Edit and close again when done.')
  }

  const addAddendum = () => run(async () => {
    const r = await api.post<{ prescription: Prescription }>(
      `/api/visits/${visitId}/prescription/addendum`, { note: addendum },
    )
    setDetail((d) => (d ? { ...d, prescription: r.prescription } : d))
    setAddendum('')
  }, 'Addendum added.')

  const saveTemplate = () => run(async () => {
    const r = await api.post<{ template: PrescriptionTemplate }>('/api/templates', {
      name: templateName,
      doctorId,
      payload: {
        medicines: payload.medicines,
        tests: payload.tests,
        advice: payload.advice,
        followUpDays: payload.followUpDays,
      },
    })
    setTemplates((t) => [r.template, ...t.filter((x) => x.id !== r.template.id)])
    setTemplateName('')
  }, 'Saved as a template.')

  function applyTemplate(t: PrescriptionTemplate) {
    setPad((p) => ({
      ...p,
      advice: t.payload.advice ?? p.advice,
      followUpDays: t.payload.followUpDays != null ? String(t.payload.followUpDays) : p.followUpDays,
      medicines: padRows([
        ...p.medicines.filter((m) => !medBlank(m)),
        ...t.payload.medicines.map((m) => ({
          key: newKey(), drugId: m.drugId, drugName: m.drugName, genericName: '',
          strength: m.strength ?? '', form: m.form ?? '',
          doseMorning: m.doseMorning ?? '', doseAfternoon: m.doseAfternoon ?? '',
          doseNight: m.doseNight ?? '', timing: (m.timing ?? '') as MedicineTiming | '',
          durationDays: m.durationDays != null ? String(m.durationDays) : '',
          quantity: m.quantity ?? '', instructions: m.instructions ?? '',
        })),
      ], medBlank, blankMed),
      tests: padRows([
        ...p.tests.filter((x) => !testBlank(x)),
        ...t.payload.tests.map((x) => ({
          key: newKey(), testId: x.testId, testName: x.testName,
          urgency: x.urgency, instructions: x.instructions ?? '', preparation: null,
        })),
      ], testBlank, blankTest),
    }))
    void api.post(`/api/templates/${t.id}/use`).catch(() => { /* ordering only */ })
    setMsg({ kind: 'ok', text: `Applied "${t.name}".` })
  }

  function repeatLast() {
    const last = history.find((h) => h.prescription)
    if (!last?.prescription) return
    const rx = last.prescription
    setPad((p) => ({
      ...p,
      advice: rx.advice ?? p.advice,
      medicines: padRows([
        ...p.medicines.filter((m) => !medBlank(m)),
        ...rx.medicines.map((m) => ({
          key: newKey(), drugId: m.drugId, drugName: m.drugName, genericName: '',
          strength: m.strength ?? '', form: m.form ?? '',
          doseMorning: m.doseMorning ?? '', doseAfternoon: m.doseAfternoon ?? '',
          doseNight: m.doseNight ?? '', timing: (m.timing ?? '') as MedicineTiming | '',
          durationDays: m.durationDays != null ? String(m.durationDays) : '',
          quantity: m.quantity ?? '', instructions: m.instructions ?? '',
        })),
      ], medBlank, blankMed),
    }))
    setMsg({ kind: 'ok', text: `Carried forward ${rx.medicines.length} medicines from ${last.visit.visitDate}.` })
  }

  const searchDrugs = useCallback(
    (q: string) => api.get<{ drugs: Drug[] }>(`/api/drugs?q=${encodeURIComponent(q)}`).then((r) => r.drugs),
    [],
  )
  const searchTests = useCallback(
    (q: string) => api.get<{ tests: TestMasterRow[] }>(`/api/tests?q=${encodeURIComponent(q)}`).then((r) => r.tests),
    [],
  )

  const latest = detail?.vitals[0] ?? null

  /**
   * Every medicine on the pad checked against what the patient is recorded as
   * allergic to, keyed by row so each line can flag itself. Warns, never
   * blocks: a doctor who knows this patient tolerates the drug still signs.
   */
  const conflicts = useMemo(() => {
    const allergies = detail?.patient.allergies
    const map = new Map<string, AllergyConflict[]>()
    if (!allergies?.trim()) return map
    for (const m of pad.medicines) {
      if (medBlank(m)) continue
      const hits = allergyConflicts(allergies, m.drugName, m.genericName)
      if (hits.length > 0) map.set(m.key, hits)
    }
    return map
  }, [detail?.patient.allergies, pad.medicines])

  /* The server refuses an empty prescription; the button should say so before
     the click rather than after it. */
  const hasSomethingToSign = useMemo(() => (
    pad.provisionalDiagnosis.trim() !== ''
    || pad.medicines.some((m) => !medBlank(m))
    || pad.tests.some((t) => !testBlank(t))
  ), [pad.provisionalDiagnosis, pad.medicines, pad.tests])

  const conflictSummary = useMemo(() => {
    const lines: string[] = []
    for (const m of pad.medicines) {
      const hits = conflicts.get(m.key)
      if (hits) lines.push(`${m.drugName.trim()} — ${describeConflicts(hits)}`)
    }
    return lines
  }, [conflicts, pad.medicines])

  return (
    <>
      <div className="page-head no-print">
        <h2>Consulting room</h2>
        {!user?.doctorId && doctorList.length > 0 && (
          <select value={doctorId} onChange={(e) => { setDoctorId(e.target.value); setParams({}, { replace: true }) }}>
            {doctorList.map((d) => <option key={d.id} value={d.id}>{d.fullName}</option>)}
          </select>
        )}
      </div>

      {msg && <div className={`msg ${msg.kind} no-print`}>{msg.text}</div>}

      <div className="console no-print">
        <aside className="queue">
          <div className="panel">
            <h3>Today&rsquo;s queue</h3>
            {queue.length === 0 && <div className="queue-empty">Nobody waiting.</div>}
            <div className="queue-list">
              {queue.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className={`queue-item${row.id === visitId ? ' is-current' : ''}${row.status === 'completed' ? ' is-done' : ''}`}
                  onClick={() => void openVisit(row)}
                >
                  <span className="queue-token">#{row.tokenNo}</span>
                  <span>
                    <span className="queue-name">{row.patientName}</span>
                    <span className="queue-sub">
                      {' '}{ageLabel(row)}/{genderShort(row.gender)} · {VISIT_TYPE_LABEL[row.visitType]}
                    </span>
                    <div className="queue-sub"><CategoryPill category={row.category} /></div>
                    {row.chiefComplaint && <div className="queue-sub">{row.chiefComplaint}</div>}
                    {row.allergies && <div className="queue-allergy">Allergy: {row.allergies}</div>}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </aside>

        <div>
          {!detail && (
            <div className="panel">
              <h3>No patient open</h3>
              <p className="panel-note">Pick a token from the queue to start a consultation.</p>
            </div>
          )}

          {detail && (
            <>
              <div className="context">
                <span className="who">{detail.patient.fullName}</span>
                <span className="bit">{ageLabel(detail.patient)} / {genderShort(detail.patient.gender)}</span>
                <span className="bit">{detail.patient.patientCode}</span>
                <span className="bit"><CategoryPill category={detail.patient.category} /></span>
                <span className="bit">
                  Visit <strong>#{detail.visit.visitNo}</strong> · {VISIT_TYPE_LABEL[detail.visit.visitType]} · token #{detail.visit.tokenNo}
                </span>
                {latest && (
                  /* This strip is where an out-of-range reading matters most,
                     and it used to be one flat unflagged line. Each value now
                     carries its own flag, named in words as well as coloured. */
                  <span className="vitals">
                    {(() => {
                      const f = vitalFlags(latest)
                      const parts: Array<{ text: string; flag?: VitalFlag }> = [
                        latest.weightKg ? { text: `Wt ${latest.weightKg}` } : null,
                        latest.bpSystolic && latest.bpDiastolic
                          ? { text: `BP ${latest.bpSystolic}/${latest.bpDiastolic}`, flag: f.bp } : null,
                        latest.pulseBpm ? { text: `Pulse ${latest.pulseBpm}`, flag: f.pulse } : null,
                        latest.temperatureF ? { text: `Temp ${latest.temperatureF}`, flag: f.temp } : null,
                        latest.spo2Pct ? { text: `SpO2 ${latest.spo2Pct}%`, flag: f.spo2 } : null,
                        latest.bloodSugar
                          ? { text: `Sugar ${latest.bloodSugar}`, flag: f.sugar } : null,
                      ].filter(Boolean) as Array<{ text: string; flag?: VitalFlag }>
                      return parts.map((p, i) => (
                        <span key={p.text} className={p.flag ? vitalClass(p.flag) : undefined}>
                          {i > 0 && '  '}
                          {p.text}
                          {p.flag && p.flag.level !== 'normal' && (
                            <span className="vital-tag"> {p.flag.label}</span>
                          )}
                        </span>
                      ))
                    })()}
                  </span>
                )}
                {detail.patient.allergies && (
                  <span className="alert">Allergies: {detail.patient.allergies}</span>
                )}
                {detail.patient.chronicConditions && (
                  <span className="bit">Chronic: {detail.patient.chronicConditions}</span>
                )}
              </div>

              {locked && (
                <div className="panel closed-panel">
                  <div className="closed-head">
                    <div>
                      <strong>Consultation closed</strong>
                      <div className="panel-note" style={{ margin: 0 }}>
                        Prescription is signed. Printed {detail.prescription?.printCount ?? 0}× so far.
                      </div>
                    </div>
                  </div>
                  <div className="closed-actions">
                    <button
                      type="button" className="primary big"
                      disabled={busy} onClick={() => void printAgain()}
                    >
                      🖨  Print prescription
                    </button>
                    <button
                      type="button" className="secondary"
                      disabled={busy} onClick={reopenForEdit}
                    >
                      Reopen &amp; edit
                    </button>
                    <button type="button" className="link" onClick={() => setPreview((v) => !v)}>
                      {preview ? 'Hide preview' : 'Preview'}
                    </button>
                  </div>
                </div>
              )}

              {!locked && templates.length > 0 && (
                <div className="panel">
                  <h3>Templates</h3>
                  <p className="panel-note">One click fills medicines, advice and follow-up.</p>
                  <div className="chips">
                    {templates.map((t) => (
                      <button key={t.id} type="button" className="chip" onClick={() => applyTemplate(t)}>
                        {t.name}{t.useCount > 0 && <span className="count">{t.useCount}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="panel">
                <h3>Clinical notes</h3>
                <div className="pad-grid">
                  <div className="field">
                    <label htmlFor="cc">Chief complaints</label>
                    <textarea
                      id="cc" rows={2} disabled={locked} value={pad.chiefComplaints}
                      onChange={(e) => setPad((p) => ({ ...p, chiefComplaints: e.target.value }))}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="hpi">History of present illness</label>
                    <textarea
                      id="hpi" rows={2} disabled={locked} value={pad.historyPresentIllness}
                      onChange={(e) => setPad((p) => ({ ...p, historyPresentIllness: e.target.value }))}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="exam">Examination findings</label>
                    <textarea
                      id="exam" rows={2} disabled={locked} value={pad.examinationFindings}
                      onChange={(e) => setPad((p) => ({ ...p, examinationFindings: e.target.value }))}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="dx">Provisional diagnosis</label>
                    <input
                      id="dx" disabled={locked} value={pad.provisionalDiagnosis}
                      onChange={(e) => setPad((p) => ({ ...p, provisionalDiagnosis: e.target.value }))}
                    />
                    <span className="hint">ICD-10 optional</span>
                  </div>
                </div>
              </div>

              <div className="panel">
                <h3>Medicines</h3>
                {conflictSummary.length > 0 && (
                  /* Assertive, because this is the one message on the pad that
                     must not wait for the doctor to happen to look at it. */
                  <div className="msg allergy-warn" role="alert" aria-live="assertive">
                    <strong>
                      Check the allergy note before signing
                      {conflictSummary.length > 1 ? ` (${conflictSummary.length} medicines)` : ''}.
                    </strong>
                    <ul>
                      {conflictSummary.map((line) => <li key={line}>{line}</li>)}
                    </ul>
                    <span className="hint">
                      Recorded allergy: {detail.patient.allergies}. This is a name match, not a
                      clinical ruling — sign anyway if you know it is safe for this patient.
                    </span>
                  </div>
                )}
                <div className="med-head">
                  <span>Medicine</span><span>Dosage (M-A-N)</span><span>Timing</span>
                  <span>Duration</span><span>Qty</span><span />
                </div>
                {pad.medicines.map((m) => {
                  const auto = computeQuantity(
                    m.doseMorning, m.doseAfternoon, m.doseNight,
                    m.durationDays ? Number(m.durationDays) : null,
                  )
                  return (
                    <div className="med-row" key={m.key}>
                      <div>
                        <Autocomplete<Drug>
                          value={m.drugName}
                          disabled={locked}
                          placeholder="Brand or generic name"
                          onChange={(v) => setMed(m.key, { drugName: v, drugId: null, genericName: '' })}
                          search={searchDrugs}
                          keyOf={(d) => d.id}
                          emptyNote="Not in the drug list — it will still print as typed."
                          render={(d) => (
                            <>
                              <div>{medicineLabel(d.brandName, d.strength)}</div>
                              {d.genericName && <div className="generic">{d.genericName}</div>}
                            </>
                          )}
                          onPick={(d) => setMed(m.key, {
                            drugId: d.id,
                            drugName: d.form ? `${d.form}. ${d.brandName}` : d.brandName,
                            genericName: d.genericName ?? '',
                            strength: d.strength ?? '',
                            form: d.form ?? '',
                            doseMorning: d.defaultDoseMorning ?? m.doseMorning,
                            doseAfternoon: d.defaultDoseAfternoon ?? m.doseAfternoon,
                            doseNight: d.defaultDoseNight ?? m.doseNight,
                            timing: d.defaultTiming ?? m.timing,
                            durationDays: d.defaultDurationDays ?? m.durationDays,
                          })}
                        />
                        {conflicts.has(m.key) && (
                          <div className="allergy-row-warn">
                            ⚠ Allergy: {describeConflicts(conflicts.get(m.key)!)}
                          </div>
                        )}
                        {!medBlank(m) && (
                          <input
                            style={{ marginTop: 5 }}
                            placeholder="Note on this medicine (optional)"
                            disabled={locked}
                            value={m.instructions}
                            onChange={(e) => setMed(m.key, { instructions: e.target.value })}
                          />
                        )}
                      </div>
                      <div className="dose-boxes">
                        <input aria-label="Morning" disabled={locked} value={m.doseMorning}
                          onChange={(e) => setMed(m.key, { doseMorning: e.target.value })} />
                        <input aria-label="Afternoon" disabled={locked} value={m.doseAfternoon}
                          onChange={(e) => setMed(m.key, { doseAfternoon: e.target.value })} />
                        <input aria-label="Night" disabled={locked} value={m.doseNight}
                          onChange={(e) => setMed(m.key, { doseNight: e.target.value })} />
                      </div>
                      <select aria-label="Timing" disabled={locked} value={m.timing}
                        onChange={(e) => setMed(m.key, { timing: e.target.value as MedicineTiming | '' })}>
                        <option value="">—</option>
                        {Object.entries(MEDICINE_TIMING_LABEL).map(([v, label]) => (
                          <option key={v} value={v}>{label}</option>
                        ))}
                      </select>
                      <input aria-label="Duration in days" inputMode="numeric" placeholder="days"
                        disabled={locked} value={m.durationDays}
                        onChange={(e) => setMed(m.key, { durationDays: e.target.value.replace(/\D/g, '') })} />
                      <input aria-label="Quantity" placeholder={auto ?? '—'}
                        disabled={locked} value={m.quantity}
                        onChange={(e) => setMed(m.key, { quantity: e.target.value })} />
                      {!medBlank(m) && !locked ? (
                        <button type="button" className="row-drop" title="Remove"
                          onClick={() => dropMed(m.key)}>&times;</button>
                      ) : <span />}
                    </div>
                  )
                })}
              </div>

              <div className="panel">
                <h3>Tests advised</h3>
                {pad.tests.map((t) => (
                  <div className="test-row" key={t.key}>
                    <div>
                      <Autocomplete<TestMasterRow>
                        value={t.testName}
                        disabled={locked}
                        placeholder="Test name"
                        onChange={(v) => setTest(t.key, { testName: v, testId: null, preparation: null })}
                        search={searchTests}
                        keyOf={(x) => x.id}
                        render={(x) => (
                          <>
                            <div>{x.name}</div>
                            {x.category && <div className="generic">{x.category}{x.preparation ? ` · ${x.preparation}` : ''}</div>}
                          </>
                        )}
                        onPick={(x) => setTest(t.key, {
                          testId: x.id, testName: x.name, preparation: x.preparation,
                        })}
                      />
                      {t.preparation && <div className="test-prep">Preparation: {t.preparation}</div>}
                    </div>
                    <select aria-label="Urgency" disabled={locked} value={t.urgency}
                      onChange={(e) => setTest(t.key, { urgency: e.target.value as TestUrgency })}>
                      {Object.entries(TEST_URGENCY_LABEL).map(([v, label]) => (
                        <option key={v} value={v}>{label}</option>
                      ))}
                    </select>
                    <input placeholder="Instruction (optional)" disabled={locked} value={t.instructions}
                      onChange={(e) => setTest(t.key, { instructions: e.target.value })} />
                    {!testBlank(t) && !locked ? (
                      <button type="button" className="row-drop" title="Remove"
                        onClick={() => dropTest(t.key)}>&times;</button>
                    ) : <span />}
                  </div>
                ))}
              </div>

              <div className="panel">
                <h3>Advice and follow-up</h3>
                <div className="pad-grid">
                  <div className="field wide">
                    <label htmlFor="advice">Advice</label>
                    <textarea id="advice" rows={2} disabled={locked} value={pad.advice}
                      onChange={(e) => setPad((p) => ({ ...p, advice: e.target.value }))} />
                    <span className="hint">Diet, activity, warning signs.</span>
                  </div>
                  <div className="field">
                    <label htmlFor="fu">Follow-up after (days)</label>
                    <input id="fu" inputMode="numeric" disabled={locked} value={pad.followUpDays}
                      onChange={(e) => setPad((p) => ({ ...p, followUpDays: e.target.value.replace(/\D/g, '') }))} />
                    {detail.prescription?.followUpDate && (
                      <span className="hint">Prints as {detail.prescription.followUpDate}</span>
                    )}
                  </div>
                </div>
              </div>

              {locked && (
                <div className="panel">
                  <h3>Addendum</h3>
                  <p className="panel-note">
                    A signed prescription is never edited. Corrections are appended and dated.
                  </p>
                  {(detail.prescription?.addenda ?? []).map((a) => (
                    <div key={a.id} className="history-entry">
                      <span className="history-date">{new Date(a.createdAt).toLocaleDateString('en-CA')}</span> {a.note}
                    </div>
                  ))}
                  <div className="field" style={{ marginTop: 10 }}>
                    <label htmlFor="add">Add a note</label>
                    <textarea id="add" rows={2} value={addendum}
                      onChange={(e) => setAddendum(e.target.value)} />
                  </div>
                  <button type="button" disabled={busy || !addendum.trim()} onClick={() => void addAddendum()}>
                    Append
                  </button>
                </div>
              )}

              {!locked && (
                <div className="panel">
                  <div className="pad-actions">
                    <button
                      type="button"
                      className="primary"
                      disabled={busy || !hasSomethingToSign}
                      title={hasSomethingToSign
                        ? undefined
                        : 'Add a diagnosis, a medicine or a test first.'}
                      onClick={() => void signAndPrint()}
                    >
                      Close &amp; print prescription
                    </button>
                    <button type="button" disabled={busy} onClick={() => void saveDraft()}>
                      Save draft
                    </button>
                    <button type="button" onClick={() => setPreview((v) => !v)}>
                      {preview ? 'Hide preview' : 'Preview'}
                    </button>
                    <span className="spacer" />
                    <input
                      placeholder="Save pad as template…"
                      value={templateName}
                      style={{ width: 200 }}
                      onChange={(e) => setTemplateName(e.target.value)}
                    />
                    <button type="button" disabled={busy || !templateName.trim()} onClick={() => void saveTemplate()}>
                      Save template
                    </button>
                  </div>
                </div>
              )}

              <div className="panel">
                <h3>Previous visits</h3>
                {history.length === 0 && <p className="panel-note">First visit to the clinic.</p>}
                {history.length > 0 && !locked && (
                  <div className="chips" style={{ marginBottom: 10 }}>
                    <button type="button" className="chip" onClick={repeatLast}>
                      Repeat last prescription
                    </button>
                  </div>
                )}
                {history.map((h) => (
                  <div className="history-entry" key={h.visit.id}>
                    <button
                      type="button"
                      className="history-head"
                      onClick={() => setOpenEntry((k) => (k === h.visit.id ? null : h.visit.id))}
                    >
                      <span>
                        <span className="history-dx">
                          {h.prescription?.provisionalDiagnosis ?? h.visit.chiefComplaint ?? 'No diagnosis recorded'}
                        </span>
                        <span className="queue-sub"> · {h.doctorName}</span>
                      </span>
                      <span className="history-date">{h.visit.visitDate}</span>
                    </button>
                    {openEntry === h.visit.id && (
                      <div className="history-body">
                        {h.prescription?.medicines.length ? (
                          <ul>
                            {h.prescription.medicines.map((m) => (
                              <li key={m.id}>
                                {medicineLabel(m.drugName, m.strength)} — {m.doseMorning ?? 0}-{m.doseAfternoon ?? 0}-{m.doseNight ?? 0}
                                {m.durationDays ? ` × ${m.durationDays} days` : ''}
                              </li>
                            ))}
                          </ul>
                        ) : <span>No medicines recorded.</span>}
                        {h.prescription?.advice && <div style={{ marginTop: 6 }}>{h.prescription.advice}</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {detail?.prescription && (
        <div className={preview ? 'rx-preview' : ''}>
          <PrescriptionPrint
            clinic={clinic}
            doctor={detail.doctor}
            patient={detail.patient}
            visit={detail.visit}
            vitals={detail.vitals}
            prescription={detail.prescription}
            letterhead={letterhead}
          />
        </div>
      )}
    </>
  )
}
